import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Eye, Send, AlertTriangle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmDestructive from '../components/ConfirmDestructive';

const REPORT_TYPES = [
  { key: 'daily',   label: 'Daily · Tue–Fri' },
  { key: 'weekly',  label: 'Weekly · Monday' },
  { key: 'monthly', label: 'Monthly · 1st Monday' },
];

// ─── Delivery log (admin visibility) ─────────────────────────────────
function DeliveryLog() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('all');
  const [reportType, setReportType] = useState('all');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const reload = async () => {
    setLoading(true);
    try {
      const params = { include_archived: includeArchived };
      if (status !== 'all') params.status = status;
      if (reportType !== 'all') params.report_type = reportType;
      const r = await api.get('/od-reports/delivery-log', { params });
      setRows(r.data.rows || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [status, reportType, includeArchived]);
  const failedCount = useMemo(() => rows.filter(r => r.status === 'failed').length, [rows]);
  const archiveResolved = async () => {
    if (archiving) return;
    if (!window.confirm('Archive every historical failed delivery row? Rows stay in the DB (audit trail intact) but stop counting toward the failed banner.')) return;
    setArchiving(true);
    try {
      const r = await api.post('/od-reports/delivery-log/archive-resolved');
      toast.success(`Archived ${r.data.archived} historical failures`);
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Archive failed'); }
    finally { setArchiving(false); }
  };
  return (
    <Card className="p-6 rounded-2xl border-[#E8E5EC] space-y-4" data-testid="delivery-log-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Mail size={16} className="text-[#685B72]" />
          <h2 className="font-serif text-xl text-[#2D2833]">Delivery log</h2>
          {failedCount > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest bg-[#8A3A3A]/15 text-[#8A3A3A]"
              data-testid="delivery-failure-banner"
            >
              <AlertTriangle size={11} /> {failedCount} failed
            </span>
          )}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <label className="flex items-center gap-1.5 text-[11px] text-[#685B72] cursor-pointer"
                 data-testid="delivery-log-include-archived-label">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={e => setIncludeArchived(e.target.checked)}
              data-testid="delivery-log-include-archived"
            />
            Show archived
          </label>
          {user?.role === 'super_user' && failedCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="text-[11px] h-8 rounded-xl"
              onClick={archiveResolved}
              disabled={archiving}
              data-testid="delivery-log-archive-resolved"
            >
              {archiving ? 'Archiving…' : 'Archive historical failures'}
            </Button>
          )}
          <Select value={reportType} onValueChange={setReportType}>
            <SelectTrigger className="w-40 rounded-xl" data-testid="delivery-log-report-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All reports</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-36 rounded-xl" data-testid="delivery-log-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="skipped_duplicate">Duplicate</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="rounded-xl border border-[#E8E5EC] overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-[#FAF8FB] text-[#685B72]">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Scheduled</th>
              <th className="px-3 py-2 text-left font-semibold">Report</th>
              <th className="px-3 py-2 text-left font-semibold">Director</th>
              <th className="px-3 py-2 text-left font-semibold">Clinics</th>
              <th className="px-3 py-2 text-left font-semibold">Period</th>
              <th className="px-3 py-2 text-left font-semibold">Status</th>
              <th className="px-3 py-2 text-left font-semibold">Attempt</th>
              <th className="px-3 py-2 text-left font-semibold">Error</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-4 text-center text-[#685B72]">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-4 text-center text-[#685B72]" data-testid="delivery-log-empty">No delivery attempts recorded yet.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={r._id || i} className="border-t border-[#E8E5EC]" data-testid={`delivery-log-row-${i}`}>
                <td className="px-3 py-1.5 font-mono text-[#685B72]">{(r.scheduled_for || '').replace('T', ' ').slice(0, 16)}</td>
                <td className="px-3 py-1.5">{r.report_type}</td>
                <td className="px-3 py-1.5">{r.director_email}</td>
                <td className="px-3 py-1.5 text-[#685B72]">{(r.clinics || []).join(', ')}</td>
                <td className="px-3 py-1.5 font-mono text-[#685B72]">{r.period_start === r.period_end ? r.period_start : `${r.period_start} → ${r.period_end}`}</td>
                <td className="px-3 py-1.5">
                  {r.status === 'sent'
                    ? <span className="px-1.5 py-0.5 rounded-full bg-[#3F544F]/20 text-[#3F544F] text-[10px] font-semibold" data-testid={`log-status-${i}`}>Sent</span>
                    : r.status === 'failed'
                      ? <span className="px-1.5 py-0.5 rounded-full bg-[#8A3A3A]/20 text-[#8A3A3A] text-[10px] font-semibold" data-testid={`log-status-${i}`}>Failed</span>
                      : r.status === 'skipped_duplicate'
                        ? <span className="px-1.5 py-0.5 rounded-full bg-[#B08A3A]/20 text-[#B08A3A] text-[10px] font-semibold" data-testid={`log-status-${i}`}>Duplicate</span>
                        : r.status === 'archived'
                          ? <span className="px-1.5 py-0.5 rounded-full bg-[#685B72]/15 text-[#685B72] text-[10px] font-semibold" data-testid={`log-status-${i}`}>Archived</span>
                          : <span className="px-1.5 py-0.5 rounded-full bg-[#685B72]/15 text-[#685B72] text-[10px] font-semibold" data-testid={`log-status-${i}`}>{r.status}</span>}
                </td>
                <td className="px-3 py-1.5 font-mono text-[#685B72]">{r.attempt_no}</td>
                <td className="px-3 py-1.5 text-[#8A3A3A] max-w-xs truncate" title={r.error || ''}>{r.error || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── Preview + manual send (super_user only) ─────────────────────────
function ManualSendCard() {
  const [directors, setDirectors] = useState([]);
  const [supers, setSupers] = useState([]);
  const [email, setEmail] = useState('');
  const [reportType, setReportType] = useState('daily');
  const [previewUrl, setPreviewUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [sendAllOpen, setSendAllOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Report families:
  //   od_*   → recipient dropdown is the OD directors list
  //   group_* → recipient dropdown is the super_users list
  const isGroup = reportType.startsWith('group_');
  const recipients = isGroup ? supers : directors;

  useEffect(() => {
    api.get('/od-reports/directors').then(r => {
      const ds = r.data.directors || [];
      const su = r.data.super_users || [];
      setDirectors(ds);
      setSupers(su);
      const pool = reportType.startsWith('group_') ? su : ds;
      if (pool[0]) setEmail(pool[0].email);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // When the family flips OD ⇄ Group, retarget the recipient field.
  useEffect(() => {
    const pool = isGroup ? supers : directors;
    if (!pool.some(r => r.email === email) && pool[0]) setEmail(pool[0].email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, directors, supers]);

  const openPreview = async () => {
    if (!email) return;
    try {
      const path = isGroup ? '/od-reports/group/preview' : '/od-reports/preview';
      const res = await api.get(path, {
        params: { report_type: reportType, email },
        responseType: 'text',
      });
      const blob = new Blob([res.data], { type: 'text/html' });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const path = isGroup ? '/od-reports/group/send-now' : '/od-reports/send-now';
      const r = await api.post(path, { report_type: reportType, email });
      setLastResult(r.data);
      toast.success(`Sent ${r.data.sent}/${r.data.attempted}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  const sendAll = async () => {
    setSendingAll(true);
    try {
      const r = await api.post('/od-reports/send-all');
      setLastResult(r.data);
      setSendAllOpen(false);
      toast.success(`All streams fired · sent ${r.data.totals?.sent || 0}/${r.data.totals?.attempted || 0}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send all failed'); }
    finally { setSendingAll(false); }
  };

  return (
    <Card className="p-6 rounded-2xl border-[#E8E5EC] space-y-4" data-testid="manual-send-card">
      <div className="flex items-center gap-2">
        <Send size={16} className="text-[#685B72]" />
        <h2 className="font-serif text-xl text-[#2D2833]">Preview &amp; manual send</h2>
      </div>
      <p className="text-sm text-[#685B72] max-w-2xl leading-relaxed">
        Reports go out automatically at 7:30 AM America/Chicago. Use this panel to QA a single report or force a full rerun after a failure.
      </p>
      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-[#685B72]">Report</label>
          <Select value={reportType} onValueChange={setReportType}>
            <SelectTrigger className="rounded-xl mt-1" data-testid="manual-report-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="daily"          data-testid="manual-report-opt-daily">OD Daily · Tue–Fri</SelectItem>
              <SelectItem value="weekly"         data-testid="manual-report-opt-weekly">OD Weekly · Mon</SelectItem>
              <SelectItem value="monthly"        data-testid="manual-report-opt-monthly">OD Monthly · 1st Mon</SelectItem>
              <SelectItem value="group_daily"    data-testid="manual-report-opt-group_daily">Group Daily · Tue–Fri (Super User)</SelectItem>
              <SelectItem value="group_weekly"   data-testid="manual-report-opt-group_weekly">Group Weekly · Mon (Super User)</SelectItem>
              <SelectItem value="group_monthly"  data-testid="manual-report-opt-group_monthly">Group Monthly · 1st Mon (Super User)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-[#685B72]">
            Recipient ({isGroup ? 'Super Users' : 'Office Directors'})
          </label>
          <Select value={email} onValueChange={setEmail}>
            <SelectTrigger className="rounded-xl mt-1" data-testid="manual-director-select"><SelectValue placeholder="Pick a recipient" /></SelectTrigger>
            <SelectContent>
              {recipients.map(d => (
                <SelectItem key={d.email} value={d.email} data-testid={`manual-director-opt-${d.email}`}>
                  {d.name || d.email}{isGroup ? '' : ` — ${(d.clinics || []).join(', ')}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={openPreview} disabled={!email} variant="outline" className="rounded-xl" data-testid="manual-preview-btn">
          <Eye size={14} className="mr-2" /> Preview
        </Button>
        <Button onClick={sendNow} disabled={!email || sending} className="bg-[#685B72] hover:bg-[#52475A] text-white rounded-xl" data-testid="manual-send-btn">
          <Send size={14} className="mr-2" /> {sending ? 'Sending…' : 'Send now to this recipient'}
        </Button>
        <Button onClick={() => setSendAllOpen(true)} disabled={sendingAll} variant="outline"
                className="rounded-xl border-[#4A3B5F] text-[#4A3B5F] hover:bg-[#4A3B5F] hover:text-white"
                data-testid="manual-send-all-btn">
          <Send size={14} className="mr-2" /> {sendingAll ? 'Firing all streams…' : 'Send all reports to all recipients'}
        </Button>
      </div>
      <ConfirmDestructive
        open={sendAllOpen}
        onOpenChange={setSendAllOpen}
        tone="warn"
        title="Send all reports to every recipient"
        description={
          <>
            This fires every OD and Super-User digest to every eligible recipient
            <b> right now</b>. Idempotency is bypassed — recipients <b>WILL</b> receive
            the emails immediately, even if today's regular batch already went out.
          </>
        }
        confirmPhrase="SEND ALL"
        confirmLabel="Send all now"
        running={sendingAll}
        onConfirm={sendAll}
        testId="confirm-send-all-digests"
      />
      {previewUrl && (
        <div className="rounded-xl border border-[#E8E5EC] overflow-hidden" data-testid="manual-preview-frame">
          <div className="px-3 py-2 flex items-center justify-between bg-[#FAF8FB]">
            <div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Preview · {reportType} · {email}</div>
            <button onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(''); }} className="text-xs text-[#685B72] hover:text-[#2D2833] underline" data-testid="manual-preview-close">Close</button>
          </div>
          <iframe title="OD report preview" src={previewUrl} sandbox="allow-same-origin" className="w-full h-[720px] bg-white" data-testid="manual-preview-iframe" />
        </div>
      )}
      {lastResult && (
        <pre className="text-[11px] bg-[#FAF8FB] p-3 rounded-xl border border-[#E8E5EC] overflow-x-auto max-h-64" data-testid="manual-send-result">
          {JSON.stringify(lastResult, null, 2)}
        </pre>
      )}
    </Card>
  );
}

// ─── Live scheduler view (iter 302) ─────────────────────────────────
function RecipientPills({ recipients }) {
  const [expanded, setExpanded] = useState(false);
  if (!recipients || recipients.length === 0) {
    return <span className="italic text-[#8A7D95]">n/a</span>;
  }
  const visible = expanded ? recipients : recipients.slice(0, 2);
  const remaining = recipients.length - visible.length;
  return (
    <div
      className="flex flex-wrap gap-1 items-center"
      data-testid="schedule-row-recipients"
      title={recipients.map(r => r.email).join('\n')}
    >
      {visible.map((r) => (
        <span
          key={r.email}
          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#F4F1F8] text-[#4A3B5F] max-w-[180px] truncate"
          data-testid={`recipient-pill-${r.email}`}
        >
          {r.name && r.name.trim() ? r.name.split(' ')[0] : r.email.split('@')[0]}
        </span>
      ))}
      {remaining > 0 && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#685B72]/10 text-[#685B72] hover:bg-[#685B72]/20"
          data-testid="recipient-pill-expand"
        >
          +{remaining} more
        </button>
      )}
      {expanded && recipients.length > 2 && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#685B72]/10 text-[#685B72] hover:bg-[#685B72]/20"
          data-testid="recipient-pill-collapse"
        >
          collapse
        </button>
      )}
    </div>
  );
}

function ScheduleView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get('/od-reports/schedules')
      .then(r => setRows(r.data.jobs || []))
      .catch(e => toast.error(e?.response?.data?.detail || 'Schedule load failed'))
      .finally(() => setLoading(false));
  }, []);
  return (
    <Card className="p-6 rounded-2xl border-[#E8E5EC] space-y-3" data-testid="schedule-view-card">
      <div className="flex items-center gap-2">
        <FileText size={16} className="text-[#685B72]" />
        <h2 className="font-serif text-xl text-[#2D2833]">Live schedules &amp; recipients</h2>
      </div>
      <p className="text-sm text-[#685B72] max-w-2xl">
        Every cron the pod is currently running, when it fires next, and who receives each report. Recipients update automatically as users are added / deactivated.
      </p>
      <div className="rounded-xl border border-[#E8E5EC] overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-[#FAF8FB] text-[#685B72]">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Job</th>
              <th className="px-3 py-2 text-left font-semibold">Fires</th>
              <th className="px-3 py-2 text-left font-semibold">Next run (UTC)</th>
              <th className="px-3 py-2 text-left font-semibold">Recipients</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-3 py-4 text-center text-[#685B72]">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-4 text-center text-[#685B72]">No scheduler jobs registered — is this pod the leader?</td></tr>
            )}
            {rows.map(j => (
              <tr key={j.id} className="border-t border-[#E8E5EC]" data-testid={`schedule-row-${j.id}`}>
                <td className="px-3 py-1.5 font-mono text-[#2D2833]">{j.id}</td>
                <td className="px-3 py-1.5 text-[#685B72]">{j.fires || j.trigger}</td>
                <td className="px-3 py-1.5 font-mono text-[#685B72]">{(j.next_run_time || '').replace('T', ' ').slice(0, 16) || '—'}</td>
                <td className="px-3 py-1.5">
                  <RecipientPills recipients={j.recipients} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── Read-only schedule summary shown to ODs & Admins ────────────────
function ScheduleSummary() {
  return (
    <Card className="p-6 rounded-2xl border-[#E8E5EC]" data-testid="schedule-summary">
      <div className="flex items-center gap-2 mb-3">
        <Mail size={16} className="text-[#685B72]" />
        <h2 className="font-serif text-xl text-[#2D2833]">Digest schedule</h2>
      </div>
      <p className="text-sm text-[#685B72] leading-relaxed mb-4">
        Automated Office Director digests. Contents, frequency, and layout are managed centrally — no per-user customization.
      </p>
      <ul className="text-sm text-[#2D2833] space-y-2">
        <li><span className="font-semibold">Daily</span> · Tuesday–Friday 7:30 AM CT · covers the previous calendar day</li>
        <li><span className="font-semibold">Weekly</span> · Every Monday 7:30 AM CT · prior Monday–Sunday vs the week before</li>
        <li><span className="font-semibold">Monthly</span> · First Monday of every month 7:30 AM CT · prior full month, sent alongside the weekly email</li>
      </ul>
      <div className="mt-4 text-xs text-[#685B72] leading-relaxed border-t border-[#E8E5EC] pt-3">
        Only completed appointments count toward session targets. Late cancellations, no-shows, and cancellations never count. Reports include counts and status only — no patient names or PHI. Contact your super user to update director emails, clinic assignments, or performance targets.
      </div>
    </Card>
  );
}

// ─── Monthly Clinic Report launcher card ───────────────────────────
function MonthlyReportLauncher({ user }) {
  // Default month = the most recently completed calendar month
  const defaultMonth = useMemo(() => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const [month, setMonth] = useState(defaultMonth);
  const [clinic, setClinic] = useState('');
  const [clinics, setClinics] = useState([]);
  useEffect(() => {
    if (user?.role === 'office_director') {
      const list = Array.from(new Set([...(user.clinics || []), user.clinic].filter(Boolean)));
      setClinics(list);
      if (list.length && !clinic) setClinic(list[0]);
    } else {
      api.get('/clinics').then((r) => {
        const list = Array.isArray(r.data) ? r.data.map((c) => c.name || c.clinic || c).filter(Boolean) : [];
        setClinics(list);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const monthOptions = useMemo(() => {
    const now = new Date();
    const out = [];
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return out;
  }, []);
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const label = (m) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;

  return (
    <Card className="p-6 rounded-2xl border-[#B08A3A] bg-[#FFFDF7]" data-testid="monthly-report-launcher">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#B08A3A]">On-demand · printable</div>
          <h2 className="font-serif text-xl text-[#2D2833] mt-1 flex items-center gap-2"><FileText size={18} className="text-[#B08A3A]" /> Generate Monthly Clinic Report</h2>
          <p className="text-sm text-[#685B72] mt-2 max-w-xl leading-relaxed">
            A structured, printable report you can review before your monthly leadership meeting. Every number is recomputed fresh — sessions vs target, show rate & attendance, utilization, staff compliance, referrals, pending notes, plus editable notes and an action-plan section.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3 mt-4">
        <div>
          <label htmlFor="mr-clinic" className="text-[10px] uppercase tracking-widest font-semibold text-[#685B72]">Clinic</label>
          <Select value={clinic} onValueChange={setClinic}>
            <SelectTrigger id="mr-clinic" className="w-48 rounded-xl mt-1" data-testid="monthly-report-clinic-select"><SelectValue placeholder="Pick clinic" /></SelectTrigger>
            <SelectContent>
              {clinics.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label htmlFor="mr-month" className="text-[10px] uppercase tracking-widest font-semibold text-[#685B72]">Report month</label>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger id="mr-month" className="w-48 rounded-xl mt-1" data-testid="monthly-report-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthOptions.map((m) => (<SelectItem key={m} value={m}>{label(m)}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <Link
          to={clinic ? `/monthly-report?clinic=${encodeURIComponent(clinic)}&month=${encodeURIComponent(month)}` : '#'}
          target={clinic ? '_blank' : undefined}
          rel="noopener noreferrer"
          onClick={(e) => { if (!clinic) e.preventDefault(); }}
          aria-disabled={!clinic}
          className={`inline-flex items-center gap-2 rounded-xl h-10 px-4 text-white font-semibold text-sm transition ${clinic ? 'bg-[#B08A3A] hover:bg-[#95742d]' : 'bg-[#B08A3A]/40 pointer-events-none cursor-not-allowed'}`}
          data-testid="monthly-report-generate-btn"
        >
          <FileText size={14} /> Generate report
        </Link>
      </div>
    </Card>
  );
}

// ─── Main page — role-gated composition ───────────────────────────────
export default function ODDigest() {
  const { user } = useAuth();
  if (!user) return null;
  const role = user.role;
  const isSuper = role === 'super_user';
  const isAdmin = role === 'admin' || role === 'super_user';
  const canGenerate = ['office_director', 'super_user', 'admin', 'hr_payroll'].includes(role);

  return (
    <div className="space-y-8 max-w-6xl mx-auto" data-testid="od-digest-page">
      <div>
        <h1 className="font-serif text-3xl text-[#2D2833] flex items-center gap-2">
          <Mail size={22} className="text-[#685B72]" /> OD Digest Reports
        </h1>
        <p className="text-sm text-[#685B72] mt-2 max-w-2xl leading-relaxed">
          Fixed-schedule reports to every active Office Director. Contents, frequency, and layout are locked centrally.
        </p>
      </div>

      {canGenerate && <MonthlyReportLauncher user={user} />}
      <ScheduleSummary />
      {isSuper && <ScheduleView />}
      {isAdmin && <DeliveryLog />}
      {isSuper && <ManualSendCard />}
    </div>
  );
}
